import classNames from "classnames";
import { FieldValue, fieldControlClass, fieldDescribedBy } from "./FieldValue";
import { LookupFieldProps } from "./LookupField.types";
import { SearchableSelect } from "./SearchableSelect";

/**
 * Lookup (entity reference) editable field, rendered as a select over already-resolved
 * candidates. Resolving them is a data-layer concern; this needs `{id, displayName}` pairs.
 */
export function LookupField({
  label,
  name,
  value,
  options,
  onChange,
  disabled,
  required,
  placeholder = "Please select",
  searchable = false,
  error,
  className,
  helpText,
  actions,
}: LookupFieldProps) {
  const control = searchable ? (
        <SearchableSelect
          id={name}
          options={options.map((option) => ({
            value: option.id,
            label: option.displayName,
          }))}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          clearable={!required}
          // Fits the field it is in; the select's own 14rem minimum spilled out of
          // narrower ones, such as a list filter.
          className={classNames("min-w-0", { "is-invalid": error })}
          ariaDescribedBy={fieldDescribedBy(name, helpText, error)}
          ariaInvalid={error ? true : undefined}
        />
  ) : (
        <select
          id={name}
          aria-describedby={fieldDescribedBy(name, helpText, error)}
          aria-invalid={error ? true : undefined}
          name={name}
          className={fieldControlClass("form-select", error)}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          required={required}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.displayName}
            </option>
          ))}
        </select>
  );

  return (
    <FieldValue label={label} htmlFor={name} required={required} helpText={helpText} error={error} className={className} labelActions={actions}>
      {control}
    </FieldValue>
  );
}
